import { supabase } from '../lib/supabase'
import type {
  PeopleDependentListItem,
  PeopleDependentRelationship,
} from '../types/peopleDependents'

function onlyDigits(value: string): string {
  return value.replace(/\D/g, '')
}

function normalizeRelationship(value: unknown): PeopleDependentRelationship {
  return value === 'Filho' ? 'Filho' : 'Cônjuge'
}

function mapListItem(row: Record<string, unknown>): PeopleDependentListItem | null {
  const dependentRaw = row.dependent_people
  const dependent = Array.isArray(dependentRaw)
    ? (dependentRaw[0] as Record<string, unknown> | undefined)
    : (dependentRaw as Record<string, unknown> | null | undefined)

  const cpf = onlyDigits(String(dependent?.tax_id_number ?? ''))
  if (cpf.length !== 11) return null

  const birthRaw = dependent?.captured_birth_date
  const birth =
    typeof birthRaw === 'string' && birthRaw.trim()
      ? birthRaw.trim().slice(0, 10)
      : null

  return {
    id: String(row.id),
    holder_people_id: Number(row.holder_people_id),
    dependent_people_id: Number(row.dependent_people_id),
    relationship: normalizeRelationship(row.relationship),
    cpf,
    name: String(dependent?.name ?? '').trim(),
    birth_date: birth,
  }
}

export const peopleDependentsService = {
  /**
   * Lista dependentes vinculados ao titular (por CPF), com nome/CPF/nascimento de people.
   */
  async listByHolderCpf(holderCpf: string): Promise<PeopleDependentListItem[]> {
    const digits = onlyDigits(holderCpf)
    if (digits.length !== 11) return []

    const { data: holder, error: holderError } = await supabase
      .from('people')
      .select('id')
      .eq('tax_id_number', digits)
      .maybeSingle()

    if (holderError) throw holderError
    if (!holder?.id) return []

    const { data, error } = await supabase
      .from('people_dependents')
      .select(
        `
        id,
        holder_people_id,
        dependent_people_id,
        relationship,
        dependent_people:people!dependent_people_id (
          id,
          name,
          tax_id_number,
          captured_birth_date
        )
      `
      )
      .eq('holder_people_id', holder.id)
      .order('created_at', { ascending: true })

    if (error) throw error

    const items: PeopleDependentListItem[] = []
    for (const row of data ?? []) {
      const item = mapListItem(row as Record<string, unknown>)
      if (item) items.push(item)
    }
    return items
  },
}
